import React from 'react'

import Flyout from 'components/SearchBar/SearchbarFlyout'
import { debounce } from 'helpers/delays'
import { inject } from 'helpers/mobx'
import SearchBarStore from 'store/features/searchBar'
import * as styles from './styles.module.scss'

interface IProps {
  searchBarStore: SearchBarStore
  translatedSearchPlaceholder: string
  trackSearchEvent: ({
    type,
    searchTerm,
  }: {
    type: string
    searchTerm: string
  }) => void
}

const DEBOUNCE_DELAY_MS = 500

class SearchBar extends React.Component<IProps> {
  private inputRef
  private fetchSuggestions
  private wrapperRef

  componentDidMount() {
    const { searchBarStore } = this.props

    this.attachDOMEvents()

    this.fetchSuggestions = debounce(
      () => searchBarStore.fetchSuggestions(searchBarStore.searchQuery),
      DEBOUNCE_DELAY_MS,
    )

    this.props.searchBarStore.getLastsSearches()
  }

  componentWillUnmount() {
    this.detachDOMEvents()
  }

  attachDOMEvents() {
    document.addEventListener('click', this.handleDocumentClick, false)
    document.addEventListener('touchend', this.handleDocumentClick, false)
  }

  detachDOMEvents() {
    document.removeEventListener('click', this.handleDocumentClick, false)
    document.removeEventListener('touchend', this.handleDocumentClick, false)
  }

  handleWrapperClick = () => {
    const { searchBarStore } = this.props
    if (!searchBarStore.isSearchBarOpen) {
      this.toggleSearchBar()
    }
  }

  handleDocumentClick = e => {
    const { searchBarStore } = this.props
    const target = e.target

    if (searchBarStore.isSearchBarOpen && !this.wrapperRef.contains(target)) {
      searchBarStore.closeFlyout()
      if (!searchBarStore.searchQuery.length) {
        this.toggleSearchBar()
      }
    }
  }

  setInputRef = inputRef => {
    this.inputRef = inputRef
  }

  setWrapperRef = ref => {
    this.wrapperRef = ref
  }

  focusInput = () => {
    const { searchBarStore } = this.props

    if (!searchBarStore.isSearchBarOpen) {
      return
    }

    this.inputRef.focus()
    if (searchBarStore.isSearchBarOpen) {
      searchBarStore.getLastsSearches()
    }
  }

  toggleSearchBar = () => {
    const { searchBarStore } = this.props
    searchBarStore.toggleSearchBar()

    if (!searchBarStore.isSearchBarOpen) {
      searchBarStore.closeFlyout()
    }

    this.focusInput()
  }

  handleTextInputSubmit = event => {
    const { searchBarStore } = this.props
    event.preventDefault()
    this.props.trackSearchEvent({
      type: 'submitted',
      searchTerm: this.inputRef.value,
    })
    searchBarStore.submitQuery(this.inputRef)
  }

  handleTextInputChange = event => {
    const { searchBarStore } = this.props

    if (!searchBarStore.isFlyoutOpen) {
      searchBarStore.openFlyout()
    }

    searchBarStore.setSearchQuery(event.target.value)
    searchBarStore.searchQuery.trim().length < 3 &&
      searchBarStore.clearSuggestions()
    searchBarStore.searchQuery.trim().length >= 3 && this.fetchSuggestions()
  }

  handleCloseIconClick = () => {
    const { searchBarStore } = this.props

    searchBarStore.clearSuggestions()

    if (searchBarStore.isSearchBarOpen && searchBarStore.searchQuery.length) {
      searchBarStore.setSearchQuery('')
      this.inputRef.focus()
      return
    }
    searchBarStore.closeFlyout()
    this.toggleSearchBar()
  }

  handleElementClick = elementTitle => {
    this.props.searchBarStore.setSearchQuery(elementTitle)
    this.props.trackSearchEvent({
      type: 'suggestion',
      searchTerm: elementTitle,
    })
    this.props.searchBarStore.closeFlyout()
  }

  renderSearchesFlyout = () => {
    const { searchBarStore } = this.props

    if (
      searchBarStore.searchQuery.trim().length < 3 &&
      searchBarStore.searchTerms.terms.length
    ) {
      return (
        <Flyout
          isOpen={searchBarStore.isFlyoutOpen}
          onElementClick={this.handleElementClick}
          lastSearches={searchBarStore.searchTerms.terms}
          isNotFoundStyle
        />
      )
    }

    if (
      searchBarStore.searchQuery.trim().length >= 3 &&
      searchBarStore.suggestedSearches.products.length
    ) {
      return (
        <Flyout
          isOpen={searchBarStore.isFlyoutOpen}
          onElementClick={this.handleElementClick}
          searchQuery={searchBarStore.searchQuery}
          suggestions={searchBarStore.suggestedSearches}
          isNotFoundStyle
        />
      )
    }
  }

  render() {
    const { searchBarStore, translatedSearchPlaceholder } = this.props

    return (
      <div
        className={styles.searchBar}
        onClick={this.handleWrapperClick}
        ref={this.setWrapperRef}
      >
        <form onSubmit={this.handleTextInputSubmit}>
          <div className={styles.textInputWrapper}>
            <input
              type="text"
              ref={this.setInputRef}
              className={styles.textInput}
              value={searchBarStore.searchQuery}
              onFocus={this.props.searchBarStore.openFlyout}
              onChange={this.handleTextInputChange}
              placeholder={translatedSearchPlaceholder}
            />
            {searchBarStore.searchQuery.length > 0 && (
              <div
                className={styles.closeIcon}
                onClick={this.handleCloseIconClick}
              />
            )}
            <div
              onClick={this.handleTextInputSubmit}
              className={styles.searchIcon}
            />
          </div>
        </form>

        {this.renderSearchesFlyout()}
      </div>
    )
  }
}

export default inject(
  store => ({
    searchBarStore: store.notFoundSearchBarStore,
    translatedSearchPlaceholder: store.t(
      'Suche jetzt nach Produkten oder Marken',
    ),
    trackSearchEvent: store.trackingStore.trackSearchEvent,
  }),
  { observe: true },
)(SearchBar)
